import { useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

type Notification = {
  id: string
  user_id: string
  title: string
  message: string
  type: 'booking' | 'announcement' | 'system' | 'message' | 'incident' | 'charge'
  is_read: boolean
  created_at: string
  data?: any
}

// Função para verificar se uma notificação foi lida no banco de dados
const checkNotificationRead = async (notificationId: string, userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('read_notifications')
      .select('id')
      .eq('user_id', userId)
      .eq('notification_id', notificationId)
      .maybeSingle()

    if (error) {
      console.error('Erro ao verificar notificação lida:', error)
      return false
    }

    return !!data
  } catch (error) {
    console.error('Erro na verificação de notificação:', error)
    return false
  }
}

// Função para traduzir status das ocorrências
const getStatusLabel = (status: string): string => {
  switch (status) {
    case "open":
      return "Aberta"
    case "approved_by_sindico":
      return "Aprovada pelo Síndico"
    case "approved_by_admin":
      return "Aprovada pela Administração"
    case "rejected_by_admin":
      return "Rejeitada pela Administração"
    case "completed":
      return "Concluída"
    case "closed":
      return "Fechada"
    case "in_progress":
      return "Em Andamento"
    default:
      return status
  }
}

export const useNotifications = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  // Realtime: invalidar notificações quando houver mudanças relevantes
  useEffect(() => {
    if (!profile?.id) return

    // Nome único de canal por usuário e instância para evitar conflitos
    const channelName = `notifications-realtime-${profile.id}-${Math.random().toString(36).slice(2)}`
    console.log("📡 Criando canal de notificações:", channelName)

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'access_requests',
          filter: profile?.condominium_id ? `condominium_id=eq.${profile.condominium_id}` : undefined as any
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] })
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'financial_charges',
          filter: profile?.condominium_id ? `condominium_id=eq.${profile.condominium_id}` : undefined as any
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [profile?.condominium_id, profile?.id, queryClient])
  
  return useQuery({
    queryKey: ["notifications", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return []

      console.log("Buscando notificações para usuário:", profile.id, "Perfil:", profile)

      // Buscar notificações já lidas do banco
      const { data: readNotificationsData } = await supabase
        .from('read_notifications')
        .select('notification_id')
        .eq('user_id', profile.id)
        .gt('expires_at', new Date().toISOString())

      const readNotificationIds = new Set(
        readNotificationsData?.map(rn => rn.notification_id) || []
      )

      const notifications: Notification[] = []

      // Buscar avisos importantes como notificações
      const { data: announcements, error: announcementsError } = await supabase
        .from("announcements")
        .select(`
          id,
          title,
          content,
          is_important,
          target_audience,
          created_at,
          expires_at,
          author:profiles!announcements_author_id_fkey(full_name, role, condominium_id)
        `)
        .order("created_at", { ascending: false })
        .limit(20)

      if (!announcementsError && announcements) {
        console.log("Avisos encontrados para notificações:", announcements)

        const announcementNotifications: Notification[] = announcements
          .filter(announcement => {
            // Verificar se o autor pertence ao mesmo condomínio do usuário
            const sameCondominium = announcement.author?.condominium_id === profile.condominium_id
            
            // Verificar se não expirou
            const notExpired = !announcement.expires_at || new Date(announcement.expires_at) > new Date()
            
            // Verificar target_audience baseado na role do usuário
            const canView = 
              announcement.target_audience === 'all' ||
              (announcement.target_audience === 'residents' && profile.role === 'morador') ||
              (announcement.target_audience === 'staff' && ['porteiro', 'sindico', 'administracao'].includes(profile.role))
            
            console.log(`Aviso ${announcement.id}:`, {
              sameCondominium,
              notExpired,
              canView,
              target_audience: announcement.target_audience,
              userRole: profile.role
            })
            
            return sameCondominium && notExpired && canView
          })
          .map(announcement => ({
            id: `announcement-${announcement.id}`,
            user_id: profile.id,
            title: `${announcement.is_important ? 'Aviso Importante' : 'Novo Aviso'}: ${announcement.title}`,
            message: announcement.content,
            type: "announcement" as const,
            is_read: readNotificationIds.has(`announcement-${announcement.id}`),
            created_at: announcement.created_at,
            data: {
              announcement_id: announcement.id,
              author: announcement.author?.full_name
            }
          }))

        notifications.push(...announcementNotifications)
      }

      // Buscar ocorrências relacionadas ao usuário - MELHORADO para incluir moradores
      let incidents: any[] | null = null
      let incidentsError = null

      if (profile.condominium_id) {
        const result = await supabase
          .from("incidents")
          .select(`
            id,
            title,
            status,
            created_at,
            updated_at,
            reported_by,
            assigned_to,
            condominium_id,
            unit_id,
            profiles!incidents_reported_by_fkey(full_name, condominium_id),
            unit:units(id, number, owner_id)
          `)
          .eq('condominium_id', profile.condominium_id)
          .order("updated_at", { ascending: false })
          .limit(20)
        incidents = result.data
        incidentsError = result.error
      }

      if (!incidentsError && incidents) {
        console.log("Ocorrências encontradas:", incidents)

        const incidentNotifications: Notification[] = incidents
          .filter(incident => {
            const isAssigned = incident.assigned_to === profile.id
            const isReporter = incident.reported_by === profile.id
            const isUnitOwner = incident.unit?.owner_id === profile.id
            
            // Administração vê apenas ocorrências aprovadas pelo síndico
            if (profile.role === 'administracao') {
              return incident.status === 'approved_by_sindico' || 
                     incident.status === 'approved_by_admin' || 
                     incident.status === 'rejected_by_admin'
            }
            
            const canSeeAsAdmin = ['sindico', 'superadmin'].includes(profile.role)
            
            console.log(`Ocorrência ${incident.id}:`, {
              isAssigned,
              isReporter, 
              isUnitOwner,
              canSeeAsAdmin,
              userRole: profile.role,
              incidentStatus: incident.status
            })
            
            return isAssigned || isReporter || isUnitOwner || canSeeAsAdmin
          })
          .map(incident => {
            const isAssigned = incident.assigned_to === profile.id
            const isReporter = incident.reported_by === profile.id
            const isUnitOwner = incident.unit?.owner_id === profile.id
            
            let title = ''
            let message = ''
            
            if (profile.role === 'administracao') {
              // Notificações específicas para administração
              if (incident.status === 'approved_by_sindico') {
                title = `Ocorrência Aprovada pelo Síndico: ${incident.title}`
                message = `A ocorrência "${incident.title}" foi aprovada pelo síndico e aguarda sua análise na unidade ${incident.unit?.number || 'área comum'}.`
              } else if (incident.status === 'approved_by_admin') {
                title = `Ocorrência Processada: ${incident.title}`
                message = `Você aprovou a ocorrência "${incident.title}" da unidade ${incident.unit?.number || 'área comum'}.`
              } else if (incident.status === 'rejected_by_admin') {
                title = `Ocorrência Rejeitada: ${incident.title}`
                message = `Você rejeitou a ocorrência "${incident.title}" da unidade ${incident.unit?.number || 'área comum'}.`
              }
            } else if (isAssigned && !isReporter) {
              // Ocorrência direcionada para o usuário
              title = `Nova Ocorrência Direcionada: ${incident.title}`
              message = `Uma ocorrência foi direcionada para você: ${incident.title}`
            } else if (isReporter) {
              // Ocorrência criada pelo usuário
              title = `Sua Ocorrência: ${incident.title}`
              message = `Status da sua ocorrência foi atualizado para: ${getStatusLabel(incident.status)}`
            } else if (isUnitOwner) {
              // Ocorrência relacionada à unidade do morador
              title = `Ocorrência na Sua Unidade: ${incident.title}`
              message = `Uma nova ocorrência foi reportada para a sua unidade ${incident.unit?.number || ''}: ${incident.title}`
            } else {
              // Para síndicos
              title = `Nova Ocorrência: ${incident.title}`
              message = `${incident.profiles?.full_name || 'Morador'} reportou: ${incident.title}`
            }

            return {
              id: `incident-${incident.id}`,
              user_id: profile.id,
              title,
              message,
              type: "incident" as const,
              is_read: readNotificationIds.has(`incident-${incident.id}`),
              created_at: incident.updated_at || incident.created_at,
              data: {
                incident_id: incident.id,
                status: incident.status
              }
            }
          })

        notifications.push(...incidentNotifications)
      }

      // Para moradores: buscar notificações de agendamentos
      if (profile.role === 'morador') {
        const { data: myBookings, error: myBookingsError } = await supabase
          .from("bookings")
          .select(`
            id,
            booking_date,
            start_time,
            end_time,
            status,
            approved_at,
            common_areas!bookings_area_id_fkey(name),
            profiles!bookings_approved_by_fkey(full_name)
          `)
          .eq("user_id", profile.id)
          .in("status", ["confirmed", "rejected"])
          .not("approved_at", "is", null)
          .order("approved_at", { ascending: false })
          .limit(10)

        if (!myBookingsError && myBookings) {
          console.log("Agendamentos do morador com status:", myBookings)
          
          const statusNotifications: Notification[] = myBookings.map(booking => ({
            id: `booking-status-${booking.id}`,
            user_id: profile.id,
            title: booking.status === 'confirmed' ? "Agendamento Aprovado" : "Agendamento Rejeitado",
            message: `Seu agendamento da ${booking.common_areas?.name} para ${new Date(booking.booking_date).toLocaleDateString('pt-BR')} às ${booking.start_time} foi ${booking.status === 'confirmed' ? 'aprovado' : 'rejeitado'}`,
            type: "booking" as const,
            is_read: readNotificationIds.has(`booking-status-${booking.id}`),
            created_at: booking.approved_at || booking.booking_date,
            data: {
              booking_id: booking.id,
              status: booking.status
            }
          }))

          notifications.push(...statusNotifications)
        }
      }

      // Para síndicos e administradores: buscar agendamentos pendentes
      if (profile.role === 'sindico' || profile.role === 'administracao') {
        const { data: pendingBookings, error: bookingsError } = await supabase
          .from("bookings")
          .select(`
            id,
            booking_date,
            start_time,
            end_time,
            created_at,
            common_areas!bookings_area_id_fkey(name),
            profiles!bookings_user_id_fkey(full_name)
          `)
          .eq("status", "pending")
          .eq("condominium_id", profile.condominium_id)
          .order("created_at", { ascending: false })

        if (!bookingsError && pendingBookings) {
          console.log("Agendamentos pendentes encontrados:", pendingBookings)
          
          const bookingNotifications: Notification[] = pendingBookings.map(booking => ({
            id: `booking-${booking.id}`,
            user_id: profile.id,
            title: "Novo Agendamento",
            message: `${booking.profiles?.full_name || 'Usuário'} solicitou agendamento da ${booking.common_areas?.name} para ${new Date(booking.booking_date).toLocaleDateString('pt-BR')} às ${booking.start_time}`,
            type: "booking" as const,
            is_read: readNotificationIds.has(`booking-${booking.id}`),
            created_at: booking.created_at,
            data: {
              booking_id: booking.id
            }
          }))

          notifications.push(...bookingNotifications)
        }
      }

      // Buscar solicitações de acesso (NOVO)
      let accessRequests: any[] | null = null
      let accessError = null

      if (profile.condominium_id) {
        const result = await supabase
          .from("access_requests")
          .select(`
            id,
            type,
            person_company,
            status,
            scheduled_date,
            scheduled_time,
            created_at,
            updated_at,
            approved_at,
            requester_id
          `)
          .eq('condominium_id', profile.condominium_id)
          .order("created_at", { ascending: false })
          .limit(20)
        accessRequests = result.data
        accessError = result.error
      }

      if (!accessError && accessRequests) {
        console.log("Solicitações de acesso encontradas:", accessRequests)
        
        const accessNotifications: Notification[] = []

        // Buscar dados dos usuários para as notificações
        for (const request of accessRequests) {
          let shouldInclude = false
          let title = ''
          let message = ''
          
          // Para moradores: ver status de suas próprias solicitações (aprovadas/rejeitadas)
          if (profile.role === 'morador' && request.requester_id === profile.id) {
            if (request.status !== 'pending' && request.approved_at) {
              shouldInclude = true
              const isApproved = request.status === 'approved'
              title = isApproved ? "🟢 Acesso Aprovado" : "🔴 Acesso Rejeitado"
              message = `Sua solicitação de acesso para ${request.person_company} foi ${isApproved ? 'aprovada' : 'rejeitada'}`
            }
          }
          
          // Para porteiros/administração: ver novas solicitações pendentes
          if (['porteiro', 'administracao', 'sindico'].includes(profile.role) && request.status === 'pending') {
            shouldInclude = true
            
            // Buscar nome do solicitante
            const { data: requesterData } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', request.requester_id)
              .single()
            
            title = "🚪 Nova Solicitação de Acesso"
            message = `${requesterData?.full_name || 'Morador'} solicitou acesso para ${request.person_company}`
          }

          if (shouldInclude && title) {
            accessNotifications.push({
              id: `access_request-${request.id}`,
              user_id: profile.id,
              title,
              message,
              type: "system" as const,
              is_read: readNotificationIds.has(`access_request-${request.id}`),
              created_at: request.approved_at || request.created_at,
              data: {
                access_request_id: request.id,
                status: request.status,
                type: request.type
              }
            })
          }
        }

        notifications.push(...accessNotifications)
      }

      // Para MORADORES: buscar cobranças novas das suas unidades
      if (profile.role === 'morador') {
        const { data: myUnits } = await supabase
          .from('units')
          .select('id')
          .or(`owner_id.eq.${profile.id},tenant_id.eq.${profile.id}`)
        
        if (myUnits && myUnits.length > 0) {
          const unitIds = myUnits.map(u => u.id)
          
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
          
          const { data: newCharges } = await supabase
            .from('financial_charges')
            .select(`
              id,
              description,
              amount_cents,
              due_date,
              status,
              created_at,
              unit:units(number, building)
            `)
            .in('unit_id', unitIds)
            .gte('created_at', thirtyDaysAgo.toISOString())
            .order('created_at', { ascending: false })
            .limit(10)
          
          if (newCharges) {
            const chargeNotifications: Notification[] = newCharges.map(charge => ({
              id: `charge-${charge.id}`,
              user_id: profile.id,
              title: "💰 Nova Cobrança",
              message: `${charge.description} - R$ ${(charge.amount_cents / 100).toFixed(2)} - Vencimento: ${new Date(charge.due_date).toLocaleDateString('pt-BR')}`,
              type: "charge" as const,
              is_read: readNotificationIds.has(`charge-${charge.id}`),
              created_at: charge.created_at,
              data: {
                charge_id: charge.id,
                amount_cents: charge.amount_cents,
                due_date: charge.due_date,
                unit: charge.unit
              }
            }))
            
            notifications.push(...chargeNotifications)
          }
        }
      }

      // Buscar mensagens novas (para notificações)
      const { data: recentMessages, error: messagesError } = await supabase
        .from("messages")
        .select(`
          id,
          content,
          created_at,
          conversation_id,
          sender:profiles!messages_sender_id_fkey(full_name),
          conversation:conversations!messages_conversation_id_fkey(
            title,
            conversation_type,
            participants:conversation_participants(
              profiles:profiles(id, full_name)
            )
          )
        `)
        .neq("sender_id", profile.id) // Não incluir mensagens próprias
        .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Últimas 24 horas
        .order("created_at", { ascending: false })
        .limit(10)

      if (!messagesError && recentMessages) {
        console.log("Mensagens recentes encontradas:", recentMessages)
        
        // Filtrar mensagens onde o usuário participa da conversa
        const userMessages = recentMessages.filter(message => {
          return message.conversation?.participants?.some(p => p.profiles?.id === profile.id)
        })

        const messageNotifications: Notification[] = userMessages.map(message => {
          const senderName = message.sender?.full_name || "Usuário"
          const conversationName = message.conversation?.title || 
            (message.conversation?.participants?.find(p => p.profiles?.id !== profile.id)?.profiles?.full_name) || 
            "Conversa"

          return {
            id: `message-${message.id}`,
            user_id: profile.id,
            title: `Nova mensagem de ${senderName}`,
            message: `${conversationName}: ${message.content.length > 50 ? message.content.substring(0, 50) + '...' : message.content}`,
            type: "message" as const,
            is_read: readNotificationIds.has(`message-${message.id}`),
            created_at: message.created_at,
            data: {
              message_id: message.id,
              conversation_id: message.conversation_id,
              sender_name: senderName
            }
          }
        })

        notifications.push(...messageNotifications)
      }

      // Ordenar por data de criação
      notifications.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      console.log("Total de notificações:", notifications.length)
      return notifications
    },
    enabled: !!profile?.id,
    refetchInterval: 30000 // Atualizar a cada 30 segundos
  })
}

export const useUnreadNotificationsCount = () => {
  const { data: notifications } = useNotifications()
  return notifications?.filter(n => !n.is_read).length || 0
}

export const useMarkNotificationAsRead = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!profile?.id) throw new Error('Usuário não autenticado')

      console.log("Marcando notificação como lida:", notificationId)
      
      const { error } = await supabase
        .from('read_notifications')
        .upsert({
          user_id: profile.id,
          notification_id: notificationId,
          read_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString() // 8 horas
        }, {
          onConflict: 'user_id,notification_id'
        })

      if (error) throw error
      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    }
  })
}
